import { or } from 'tio-ui/utilities';
import Button from 'tio-ui/components/button';
import Collapsible from 'tio-ui/components/collapsible';
import { LinkTo } from '@ember/routing';
import { PhTrendUp, PhKey, PhMagnifyingGlass, PhCaretDown } from 'ember-phosphor-icons';
import { Section, VStack } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import AlertBanner from 'tio-ui/components/alert-banner';
import Component from '@glimmer/component';
import BorrowerForm from 'tio-employee/components/pslf/dashboard/start-new/borrower-form.gts';
import screenIs from 'tio-common/helpers/screen-is';
import TioPageBreadcrumbs from 'tio-common/components/tio/page/breadcrumbs';
import type { IntlService } from 'ember-intl';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type PartnerService from 'tio-employee/services/partner';
import type { PslfGroup as PslfGroupModel } from 'tio-data/data/resources/pslf-group.type';
import type { PslfTemplateExtension as PslfTemplate } from 'tio-data/data/resources/pslf-template.ext';
import type RouterService from '@ember/routing/router-service';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import pageTitle from 'ember-page-title/helpers/page-title';

export interface S {
  Args: {
    model: {
      employee: EmployeeModel;
      groups: PslfGroupModel[];
      pslfTemplate?: PslfTemplate;
    };
  };
}

export default class PslfDashboardStartNewRoute extends Component<S> {
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare partner: PartnerService;
  @service declare store: StoreService;

  @tracked isOpenPslfSimplified = false;

  toggleIsOpenPslfSimplified = () => {
    this.isOpenPslfSimplified = !this.isOpenPslfSimplified;
  };

  <template>
    {{pageTitle "Start New" prepend=false}}
    <TioPageBreadcrumbs class="mb-4" as |b|>
      <b.crumb @route="authenticated.pslf.dashboard.index" @label={{t "dashboard"}} />
      <b.crumb
        @route="authenticated.pslf.dashboard.start-new"
        @label={{t "pslf.start_new.start_new_form"}}
      />
    </TioPageBreadcrumbs>
    {{#if @model.employee.pslfProfile.feinIsRogue}}
      <Section>
        <AlertBanner @intent="warning">
          <VStack class="pt-0">
            <p class="font-semibold">{{t "pslf.start_new.rogue_fein.title"}}</p>
            <p>{{t "pslf.start_new.rogue_fein.message"}}</p>
            <p>
              <LinkTo
                @route="authenticated.repaying-student-debt.coaching.schedule"
                class="font-semibold app-text-link"
              >
                {{t "pslf.start_new.rogue_fein.schedule_coaching"}}
              </LinkTo>
            </p>
          </VStack>
        </AlertBanner>
      </Section>
    {{else}}
      <VStack class="xl:flex-row">
        <Section
          class="tio-bg-brand-soft p-6 xl:p-12 xl:w-2/5 xl:shrink-0 xl:border-0 xl:rounded-r-none xl:sticky xl:top-0 xl:max-h-screen xl:overflow-y-auto"
        >
          {{! Custom collapsible functionality because of responsiveness bugs with sectional component with @collapsed arg }}
          <div class="p-4 tio-row-flush flex-row gap-x-2">
            <h2 class="font-semibold">{{t "pslf.start_new.simplified.header"}}</h2>
            {{#unless (screenIs "xl")}}
              <Button
                @appearance="minimal"
                @size="sm"
                {{on "click" this.toggleIsOpenPslfSimplified}}
              >
                <PhCaretDown
                  class="{{if this.isOpenPslfSimplified 'rotate-0' '-rotate-90'}}
                    w-4 h-4 tio-fg-info"
                />
              </Button>
            {{/unless}}
          </div>
          <Collapsible @isOpen={{or this.isOpenPslfSimplified (screenIs "xl")}}>
            <VStack>
              <div class="flex space-x-2">
                <PhTrendUp @size="20" />
                <span class="tio-text-sm-semibold">{{t
                    "pslf.start_new.simplified.stay_on_track_header"
                  }}</span>
              </div>
              <p class="text-sm tio-fg-primary">
                {{t "pslf.start_new.simplified.stay_on_track_body"}}
              </p>
            </VStack>
            <VStack>
              <div class="flex space-x-2">
                <PhKey @size="20" />
                <span class="tio-text-sm-semibold">{{t
                    "pslf.start_new.simplified.key_advantages_header"
                  }}</span>
              </div>
              <ul class="text-sm tio-fg-primary list-disc ml-5 space-y-2">
                <li>{{t "pslf.start_new.simplified.key_advantages_item_1"}}</li>
                <li>{{t "pslf.start_new.simplified.key_advantages_item_2"}}</li>
                <li>{{t "pslf.start_new.simplified.key_advantages_item_3"}}</li>
                <li>{{t "pslf.start_new.simplified.key_advantages_item_4"}}</li>
              </ul>
            </VStack>
            <VStack>
              <div class="flex space-x-2">
                <PhMagnifyingGlass @size="20" />
                <span class="tio-text-sm-semibold">{{t
                    "pslf.start_new.simplified.how_it_works_header"
                  }}</span>
              </div>
              <p class="text-sm tio-fg-primary">
                {{t "pslf.start_new.simplified.how_it_works_body"}}
              </p>
            </VStack>
            <p class="text-sm tio-fg-primary">
              {{t "pslf.start_new.simplified.for_more_info"}}
              <LinkTo
                @route="authenticated.repaying-student-debt.loan-forgiveness.index"
                target="_blank"
                rel="noopener noreferrer"
                class="app-text-link"
              >{{t "click_here"}}</LinkTo>
            </p>

          </Collapsible>
        </Section>

        <Section class="xl:border-0">
          {{#if @model.pslfTemplate}}
            <BorrowerForm @template={{@model.pslfTemplate}} @employee={{@model.employee}} />
          {{else}}
            <AlertBanner @intent="warning">
              <VStack class="pt-0">
                <p class="font-semibold">{{t "pslf.start_new.template_unavailable_title"}}</p>
                <p>{{t "pslf.start_new.template_unavailable_message"}}</p>
              </VStack>
            </AlertBanner>
          {{/if}}
        </Section>
      </VStack>
    {{/if}}
  </template>
}
